import { useEffect, useState } from 'react';

import { XMarkIcon } from '@heroicons/react/20/solid';
import { TruckIcon } from '@heroicons/react/24/outline';

import type { IChannel } from '@boc/framework/channels/types';

/**
 * Order value that unlocks free shipping, per channel. Channels missing from this map aren't
 * running the promotion and render no bar at all.
 */
const FREE_SHIPPING_THRESHOLDS: Record<string, { amount: number; currency: string }> = {
  'jsn-in': { amount: 2000, currency: 'INR' },
  'jsn-intl': { amount: 150, currency: 'USD' },
};

/**
 * Dismissal is keyed by the promotion itself, so raising the threshold (or launching a different
 * one) re-shows the bar to everyone who dismissed the previous version.
 */
const dismissalKey = (channelSlug: string, amount: number) =>
  `jsn-promo-dismissed:free-shipping:${channelSlug}:${amount}`;

/**
 * Site-wide strip advertising the free shipping threshold, dismissable per visitor.
 */
export function FreeShippingBar({ channel }: { channel: IChannel }) {
  const promotion = FREE_SHIPPING_THRESHOLDS[channel.slug];
  const [visible, setVisible] = useState(false);

  // Read on the client only. The server can't know whether this visitor dismissed the bar, so
  // rendering it during SSR would guarantee a hydration mismatch.
  useEffect(() => {
    if (!promotion) return;
    if (localStorage.getItem(dismissalKey(channel.slug, promotion.amount))) return;

    setVisible(true);
  }, [channel.slug, promotion]);

  if (!promotion || !visible) {
    return null;
  }

  const threshold = promotion.amount.toLocaleString('en', {
    style: 'currency',
    currency: promotion.currency,
    maximumFractionDigits: 0,
  });

  const dismiss = () => {
    localStorage.setItem(dismissalKey(channel.slug, promotion.amount), String(Date.now()));
    setVisible(false);
  };

  // Deliberately not sticky, unlike NotificationBar: the header doesn't stick either, so a
  // pinned promo would end up floating over the page on its own.
  return (
    <div className="bg-primary-2-60 p-2">
      <div className="text-neutral-1 relative mx-auto flex max-w-screen-xl items-center justify-center gap-x-2 px-10">
        <TruckIcon className="h-5 w-5 shrink-0" />
        <p className="text-sm sm:text-base">
          Free shipping on all orders over <b>{threshold}</b>
        </p>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss free shipping notice"
          className="absolute right-0 rounded p-1 hover:bg-white/15"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
